<template>
  <b-offcanvas
    v-model="forwardVisible"
    no-trap
    placement="end"
    no-header
    bg-variant="sidebar"
    text-variant="sidebar"
    class="form-drawer-sidebar"
    no-backdrop
    :no-close-on-route-change="noCloseOnRouteChange"
    :width="width"
    :style="stackStyle"
  >
    <div class="form-drawer-content">
      <slot></slot>

      <hr/>

      <slot name="footer-above-buttons"></slot>

      <slot name="footer-buttons">
        <b-button
          @click="save"
          :disabled="saveDisabled"
          variant="primary"
          class="action save"
          size="sm"
        >
          <font-awesome-icon :icon="saving ? 'circle-notch' : 'circle-check'" :spin="saving" />
          {{ saveText }}
        </b-button>
        <b-button
          @click="cancel"
          :disabled="cancelDisabled"
          variant="secondary"
          class="action cancel"
          size="sm"
        >
          <font-awesome-icon icon="circle-xmark" />
          {{ cancelText }}
        </b-button>
      </slot>

      <slot name="footer-below-buttons"></slot>

      <mask-spinner v-show="mask" />
    </div>
  </b-offcanvas>
</template>

<script>
import { shallowReactive } from 'vue'
import MaskSpinner from './MaskSpinner.vue'

// Every drawer is an offcanvas panel teleported to <body> with the same z-index, so
// without help they paint in DOM order, which is merely the order they happened to
// mount in (e.g. whichever master-detail list finished loading first). Track the open
// drawers so the most recently opened one is always on top.
const openDrawerIds = shallowReactive([])
let nextDrawerId = 0

// bootstrap's --bs-offcanvas-zindex, which sits below --bs-modal-zindex (1055) so a
// modal opened from a drawer still covers it.
const BASE_Z_INDEX = 1045

// A drawer nested inside a modal has to clear the modal instead. Anything that must in
// turn stay above such a drawer (HoverIcon, DatePicker, IsoDurationSingleInput) starts
// at 1156, which is what caps the stack below.
const MODAL_BASE_Z_INDEX = 1150

// How far a drawer may climb above its base. In practice only a couple of drawers are
// ever open at once, so this is just a safety net keeping us under the next layer up.
const MAX_STACK_OFFSET = 5

const removeFromStack = drawerId => {
  const index = openDrawerIds.indexOf(drawerId)
  if (index >= 0) openDrawerIds.splice(index, 1)
}

export default {
  components: {
    MaskSpinner
  },
  data () {
    return {
      height: undefined,
      drawerId: ++nextDrawerId
    }
  },
  props: {
    visible: Boolean,
    saveText: {
      type: String,
      default: 'Save'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    cancelDisabled: {
      type: Boolean,
      default: false
    },
    mask: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    noCloseOnRouteChange: {
      type: Boolean,
      default: false
    },
    // Set when the drawer is rendered inside a modal, so it stacks above it.
    inModal: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: '320px'
    }
  },
  emits: ['cancelForm', 'saveForm', 'update:visible'],
  computed: {
    stackStyle () {
      const index = openDrawerIds.indexOf(this.drawerId)
      if (index < 0) return null
      const base = this.inModal ? MODAL_BASE_Z_INDEX : BASE_Z_INDEX
      // Inline rather than via --bs-offcanvas-zindex, so it also wins against a
      // z-index utility class applied to the drawer.
      return { zIndex: base + Math.min(index, MAX_STACK_OFFSET) }
    },
    forwardVisible: {
			get () {
				return this.visible
			},
			set (v) {
				this.$emit('update:visible', v)
			}
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (visible) {
        if (!visible) {
          removeFromStack(this.drawerId)
        } else if (!openDrawerIds.includes(this.drawerId)) {
          openDrawerIds.push(this.drawerId)
        }
      }
    }
  },
  unmounted () {
    removeFromStack(this.drawerId)
  },
  methods: {
    // Raise this drawer above every other open one. Watching `visible` isn't enough on
    // its own: reopening the form for a different row leaves the drawer visible the
    // whole time, so nothing would otherwise move it back to the top of the stack.
    bringToFront () {
      removeFromStack(this.drawerId)
      openDrawerIds.push(this.drawerId)
    },
    save () {
      this.$emit('saveForm')
    },
    cancel () {
      this.$emit('cancelForm')
    }
  }
}
</script>

<style>
.form-drawer-sidebar {
  /* top should be under header */
  margin-top: var(--legacy-header-height);

  /* and bottom should be above footer */
  height: calc(100vh - var(--legacy-header-height) - var(--legacy-footer-height));

  border-left: 1px solid var(--color-slate-800) !important;
  background-color: var(--color-slate-900) !important;
  color: var(--color-slate-100) !important;

  .form-drawer-content {
    padding: 15px;
    opacity: 1.0;

    /* Specifing global for action selector enables styling for scope overrides */
    .action {
      margin-top: 25px;
      &.cancel {
        margin-left: 10px;
      }
    }
  }

  hr {
    border-color: var(--color-slate-800) !important;
    opacity: 0.3;
  }
}
</style>
